import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent } from '../ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  CalendarCheck, Bell, Pencil, CheckCircle, Loader2,
  TrendingUp, TrendingDown, Camera, MessageSquare, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, X, ArrowLeft
} from 'lucide-react';
import { useRoutableDialog } from '../../hooks/useRoutableDialog';
import { useConfirm } from '../../hooks/useConfirm';
import client from '../../api/client';
import { format, formatDistanceToNow, isPast, parseISO } from 'date-fns';
import CheckInList from './checkins/CheckInList';
import CheckInDetail from './checkins/CheckInDetail';
import CheckInTrendChart from './checkins/CheckInTrendChart';

export const MOOD_EMOJI = ['', '😞', '😕', '😐', '😊', '😄'];
export const FREQ_OPTIONS = [
  { value: 7, labelKey: 'clientCheckIns.freqWeekly' },
  { value: 14, labelKey: 'clientCheckIns.freqBiWeekly' },
  { value: 21, labelKey: 'clientCheckIns.freqThreeWeeks' },
  { value: 28, labelKey: 'clientCheckIns.freqMonthly' },
];

export function useCountdown(targetDate) {
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    const update = () => {
      if (!targetDate) { setCountdown(''); return; }
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) { setCountdown('Overdue'); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      setCountdown(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`);
    };
    update();
    const id = setInterval(update, 60000);
    return () => clearInterval(id);
  }, [targetDate]);
  return countdown;
}

function ClientCheckInsSheet({ client: clientProp, open, onClose, targetCheckInId }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [scheduleCollapsed, setScheduleCollapsed] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const rowRefs = useRef({});
  const [cmpPose, setCmpPose] = useState('front');
  const [cmpBeforeId, setCmpBeforeId] = useState('');
  const [cmpAfterId, setCmpAfterId] = useState('');
  const [lightboxPhotos, setLightboxPhotos] = useState(null); // [{ url, label }, ...] | null
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const touchStartX = useRef(null);

  // Schedule edit
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ frequency_days: 7, next_check_in: '' });

  const { data: checkInsData, isLoading: loading, isError: checkInsError } = useQuery({
    queryKey: ['client-check-ins', clientProp?.id],
    queryFn: async () => {
      const [mRes, clientDetailRes] = await Promise.all([
        client.get(`/measurements?user_id=${clientProp.id}`),
        client.get(`/users/client/${clientProp.id}/details`)
      ]);
      const allCheckIns = mRes.data;
      const clientUser = clientDetailRes.data?.user || {};
      const sched = clientUser.check_in_schedule || null;
      return { checkIns: allCheckIns, schedule: sched };
    },
    enabled: !!(open && clientProp),
  });

  const checkIns = checkInsData?.checkIns || [];
  const schedule = checkInsData?.schedule || null;

  const { id: ciId, open: openCi, close: closeCi } = useRoutableDialog('ci');
  const selectedCheckIn = useMemo(() => {
    if (!checkIns.length) return null;
    if (ciId) {
      const match = checkIns.find(c => c.id === ciId);
      if (match) return match;
    }
    return checkIns[0];
  }, [checkIns, ciId]);

  // Auto-correct stale ?ci= param to latest check-in
  useEffect(() => {
    if (!open || !checkIns.length || !ciId) return;
    const exists = checkIns.some(c => c.id === ciId);
    if (!exists) openCi(checkIns[0].id);
  }, [open, checkIns, ciId, openCi]);

  useEffect(() => {
    if (checkInsError) toast.error(t('clientCheckIns.toastLoadError'));
  }, [checkInsError, t]);

  useEffect(() => {
    if (!checkInsData) return;
    if (checkInsData.schedule) {
      setScheduleForm({
        frequency_days: checkInsData.schedule.frequency_days || 7,
        next_check_in: checkInsData.schedule.next_check_in ? checkInsData.schedule.next_check_in.slice(0, 16) : ''
      });
    }
    const withPhotos = checkInsData.checkIns.filter(ci => ci.has_photos);
    if (withPhotos.length >= 2) {
      setCmpAfterId(withPhotos[0].id);
      setCmpBeforeId(withPhotos[withPhotos.length - 1].id);
    }
  }, [checkInsData]);

  // Scroll to and expand a target check-in when opened from inbox; also sync to ?ci= param.
  // Apply ONCE per targetCheckInId — without the ref guard this re-fires whenever the user
  // clicks a different history row (ciId changes) and snaps the selection back to the target,
  // making the history list appear unclickable.
  const appliedTargetRef = useRef(null);
  useEffect(() => {
    if (!targetCheckInId || !checkIns.length) return;
    if (appliedTargetRef.current === targetCheckInId) return;
    const exists = checkIns.some(c => c.id === targetCheckInId);
    if (!exists) return;
    appliedTargetRef.current = targetCheckInId;
    if (ciId !== targetCheckInId) {
      openCi(targetCheckInId);
    }
    setExpandedId(targetCheckInId);
    setTimeout(() => {
      // Both the mobile chip strip and desktop list register refs (keyed by variant);
      // scroll whichever one is actually visible (offsetParent is null when display:none).
      [`desktop:${targetCheckInId}`, `mobile:${targetCheckInId}`].forEach(key => {
        const el = rowRefs.current[key];
        if (el && el.offsetParent !== null) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }, 150);
  }, [targetCheckInId, checkIns, ciId, openCi]);

  const countdown = useCountdown(schedule?.next_check_in);

  useEffect(() => {
    if (!lightboxPhotos) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') setLightboxIndex(i => Math.min(i + 1, lightboxPhotos.length - 1));
      if (e.key === 'ArrowLeft')  setLightboxIndex(i => Math.max(i - 1, 0));
      if (e.key === 'Escape')     setLightboxPhotos(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxPhotos]);

  const saveScheduleMutation = useMutation({
    mutationFn: () => client.put(`/measurements/schedule/${clientProp.id}`, {
      enabled: true,
      frequency_days: Number(scheduleForm.frequency_days),
      next_check_in: scheduleForm.next_check_in ? new Date(scheduleForm.next_check_in).toISOString() : null
    }),
    onSuccess: () => {
      toast.success(`Schedule updated for ${clientProp.name}`);
      setEditingSchedule(false);
      queryClient.invalidateQueries({ queryKey: ['client-check-ins', clientProp?.id] });
    },
    onError: () => { toast.error(t('clientCheckIns.toastScheduleUpdateError')); },
  });

  const removeScheduleMutation = useMutation({
    mutationFn: () => client.delete(`/measurements/schedule/${clientProp.id}`),
    onSuccess: () => {
      toast.success(`Schedule removed for ${clientProp.name}`);
      setEditingSchedule(false);
      queryClient.invalidateQueries({ queryKey: ['client-check-ins', clientProp?.id] });
    },
    onError: () => { toast.error(t('clientCheckIns.toastScheduleRemoveError')); },
  });

  const skipCheckInMutation = useMutation({
    mutationFn: () => client.post(`/measurements/skip-checkin/${clientProp.id}`, { reason: null }),
    onSuccess: () => {
      toast.success(t('clientCheckIns.toastSkipSuccess'));
      queryClient.invalidateQueries({ queryKey: ['client-check-ins', clientProp?.id] });
      queryClient.invalidateQueries({ queryKey: ['pendingReviews'] });
      queryClient.invalidateQueries({ queryKey: ['my-clients'] });
    },
    onError: () => { toast.error(t('clientCheckIns.toastSkipError')); },
  });

  const saveSchedule = () => saveScheduleMutation.mutate();
  const savingSched = saveScheduleMutation.isPending;
  const removingSched = removeScheduleMutation.isPending;
  const skipping = skipCheckInMutation.isPending;

  const handleSaveFeedback = useCallback(async (checkInId, feedbackData) => {
    await client.put(`/measurements/${checkInId}/coach-notes`, feedbackData);
    queryClient.invalidateQueries({ queryKey: ['client-check-ins', clientProp?.id] });
    queryClient.invalidateQueries({ queryKey: ['pendingReviews'] });
  }, [clientProp?.id, queryClient]);

  const checkInsWithPhotos = checkIns.filter(ci => ci.has_photos);
  const cmpBefore = checkIns.find(ci => ci.id === cmpBeforeId);
  const cmpAfter  = checkIns.find(ci => ci.id === cmpAfterId);

  // Match DashboardPage / MeasurementsPage: overdue only 24h past scheduled time; otherwise "Due today"
  const hoursUntilDue = schedule?.next_check_in
    ? (parseISO(schedule.next_check_in).getTime() - Date.now()) / 3600000
    : null;
  const isOverdue = hoursUntilDue != null && hoursUntilDue <= -24;
  const isDueToday = hoursUntilDue != null && hoursUntilDue <= 0 && hoursUntilDue > -24;

  const handleClose = useCallback(() => {
    if (ciId) closeCi();
    onClose();
  }, [ciId, closeCi, onClose]);

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="w-full sm:!max-w-none md:w-screen p-0 flex flex-col" data-testid="client-checkins-sheet">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border/50 flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            aria-label={t('common.back')}
            data-testid="checkins-back-btn"
            className="-ml-2 flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 md:p-1.5 rounded-lg hover:bg-muted/50 active:bg-muted transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <CalendarCheck className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-base leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              CHECK-<span className="text-primary">INS</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{clientProp?.name}</p>
          </div>
        </div>

        <div className="md:hidden flex-1 overflow-y-auto flex flex-col">
          {/* Collapsible schedule */}
          <div className="border-b border-border/50">
            <button
              type="button"
              onClick={() => setScheduleCollapsed(c => !c)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
              data-testid="mobile-schedule-toggle"
            >
              <Bell className={`h-4 w-4 ${isOverdue ? 'text-red-500' : 'text-primary'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground leading-none">
                  {t('clientCheckIns.scheduleTitle')}
                </p>
                {schedule?.next_check_in && (
                  <p className={`text-sm font-bold font-mono mt-0.5 ${isOverdue ? 'text-red-500' : isDueToday ? 'text-primary' : ''}`}>
                    {isOverdue ? t('clientCheckIns.overdue') : isDueToday ? t('clientCheckIns.dueToday') : countdown}
                  </p>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground">({checkIns.length})</span>
              {scheduleCollapsed ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
            </button>
            {!scheduleCollapsed && (
              <div className="px-4 pb-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Bell className={`h-4 w-4 ${isOverdue ? 'text-red-500' : 'text-primary'}`} />
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('clientCheckIns.scheduleTitle')}</span>
                  </div>
                  <button onClick={() => setEditingSchedule(!editingSchedule)}
                    className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                    <Pencil className="h-3 w-3" /> {editingSchedule ? t('clientCheckIns.cancel') : t('clientCheckIns.edit')}
                  </button>
                </div>

                {editingSchedule ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('clientCheckIns.labelFrequency')}</Label>
                        <Select value={String(scheduleForm.frequency_days)} onValueChange={v => setScheduleForm(p => ({ ...p, frequency_days: Number(v) }))}>
                          <SelectTrigger className="h-11 md:h-8 text-xs mt-0.5"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{t(o.labelKey)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-base md:text-xs text-muted-foreground">{t('clientCheckIns.labelNextCheckIn')}</Label>
                        <Input type="datetime-local" value={scheduleForm.next_check_in} className="h-10 md:h-8 text-sm md:text-xs mt-0.5"
                          onChange={e => setScheduleForm(p => ({ ...p, next_check_in: e.target.value }))} />
                      </div>
                    </div>
                    <Button size="sm" className="w-full h-11 md:h-8 bg-primary hover:bg-primary/90" onClick={saveSchedule} disabled={savingSched || removingSched}>
                      {savingSched ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle className="h-3.5 w-3.5 mr-1.5" />{t('clientCheckIns.saveSchedule')}</>}
                    </Button>
                    {schedule?.enabled && (
                      <Button size="sm" variant="ghost" className="w-full h-11 md:h-8 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        onClick={async () => {
                          const ok = await confirm({
                            title: t('clientCheckIns.confirmRemoveSchedule'),
                            confirmLabel: t('clientCheckIns.removeSchedule'),
                            cancelLabel: t('common.cancel'),
                            destructive: true,
                          });
                          if (ok) removeScheduleMutation.mutate();
                        }}
                        disabled={savingSched || removingSched}>
                        {removingSched ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1.5" />{t('clientCheckIns.removeSchedule')}</>}
                      </Button>
                    )}
                  </div>
                ) : schedule?.next_check_in ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-2xl font-bold font-mono ${isOverdue ? 'text-red-500' : isDueToday ? 'text-primary' : ''}`}>
                        {isOverdue ? t('clientCheckIns.overdue') : isDueToday ? t('clientCheckIns.dueToday') : countdown}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {format(parseISO(schedule.next_check_in), 'dd MMM yyyy · HH:mm')}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t('clientCheckIns.everyNDays', { days: schedule.frequency_days, total: checkIns.length })}</p>
                      {(isDueToday || isOverdue) && (
                        <button
                          type="button"
                          onClick={() => {
                            confirm({
                              title: t('clientCheckIns.confirmSkipCheckin'),
                              confirmLabel: t('clientCheckIns.skipThisCheckin'),
                              cancelLabel: t('common.cancel'),
                            }).then((ok) => { if (ok) skipCheckInMutation.mutate(); });
                          }}
                          disabled={skipping}
                          className="mt-2 text-[11px] text-muted-foreground/70 hover:text-muted-foreground underline underline-offset-2 disabled:opacity-50"
                          data-testid="coach-skip-checkin-btn"
                        >
                          {skipping ? <Loader2 className="h-3 w-3 animate-spin inline" /> : t('clientCheckIns.skipThisCheckin')}
                        </button>
                      )}
                    </div>
                    <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 ${isOverdue ? 'border-red-500 bg-red-500/10' : 'border-primary bg-primary/10'}`}>
                      <span className={`text-xl font-bold ${isOverdue ? 'text-red-500' : 'text-primary'}`}>{checkIns.length}</span>
                      <span className="text-[9px] text-muted-foreground leading-none">{t('clientCheckIns.checkInsCount')}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-sm text-muted-foreground">{t('clientCheckIns.noSchedule')}</p>
                    <button onClick={() => setEditingSchedule(true)} className="text-xs text-primary font-medium mt-1 hover:underline">
                      {t('clientCheckIns.setSchedule')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sticky chip strip */}
          {checkIns.length > 0 && (
            <div className="sticky top-0 bg-background z-10 border-b border-border/50">
              <CheckInList
                checkIns={checkIns}
                selectedId={selectedCheckIn?.id}
                onSelect={(id) => openCi(id)}
                variant="mobile"
                rowRefs={rowRefs}
              />
            </div>
          )}

          {/* Detail */}
          <div className="flex-1 p-4">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : selectedCheckIn ? (
              <>
                <div className="flex items-center gap-3 pb-3 border-b border-border/50">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted/60 flex flex-col items-center justify-center">
                    <span className="text-xs font-bold leading-none">{selectedCheckIn.date?.slice(8, 10)}</span>
                    <span className="text-[10px] text-muted-foreground uppercase">{selectedCheckIn.date ? format(new Date(selectedCheckIn.date), 'MMM') : ''}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                    {selectedCheckIn.weight_kg && <span className="text-base font-bold font-mono">{selectedCheckIn.weight_kg}kg</span>}
                    {selectedCheckIn.mood && <span className="text-lg">{MOOD_EMOJI[selectedCheckIn.mood]}</span>}
                    {selectedCheckIn.adherence_percent != null && (
                      <Badge variant="outline" className="text-[10px]">{selectedCheckIn.adherence_percent}%</Badge>
                    )}
                  </div>
                </div>
                <div className="mt-4">
                  <CheckInDetail
                    selectedCheckIn={selectedCheckIn}
                    allCheckIns={checkIns}
                    clientName={clientProp?.name}
                    clientId={clientProp?.id}
                    onSaveFeedback={handleSaveFeedback}
                    onOpenLightbox={(photos, index) => {
                      setLightboxPhotos(photos);
                      setLightboxIndex(index);
                    }}
                    onSelect={(id) => openCi(id)}
                  />
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 rounded-2xl border border-dashed border-border/60">
                <CalendarCheck className="h-10 w-10 text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">{t('clientCheckIns.noCheckIns')}</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Desktop 2-column layout (md: and up) ──────── */}
        <div className="hidden md:grid md:grid-cols-[360px_1fr] flex-1 overflow-hidden">
          {/* Left sidebar */}
          <aside className="border-r border-border/50 overflow-y-auto p-5 space-y-5">
            {/* SCHEDULE CARD */}
            <div className={`rounded-2xl p-4 border ${isOverdue ? 'bg-red-500/8 border-red-500/30' : 'bg-primary/10 border-primary/30'}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Bell className={`h-4 w-4 ${isOverdue ? 'text-red-500' : 'text-primary'}`} />
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t('clientCheckIns.scheduleTitle')}</span>
                </div>
                <button onClick={() => setEditingSchedule(!editingSchedule)}
                  className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                  <Pencil className="h-3 w-3" /> {editingSchedule ? t('clientCheckIns.cancel') : t('clientCheckIns.edit')}
                </button>
              </div>

              {editingSchedule ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">{t('clientCheckIns.labelFrequency')}</Label>
                      <Select value={String(scheduleForm.frequency_days)} onValueChange={v => setScheduleForm(p => ({ ...p, frequency_days: Number(v) }))}>
                        <SelectTrigger className="h-11 md:h-8 text-xs mt-0.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={String(o.value)}>{t(o.labelKey)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-base md:text-xs text-muted-foreground">{t('clientCheckIns.labelNextCheckIn')}</Label>
                      <Input type="datetime-local" value={scheduleForm.next_check_in} className="h-10 md:h-8 text-sm md:text-xs mt-0.5"
                        onChange={e => setScheduleForm(p => ({ ...p, next_check_in: e.target.value }))} />
                    </div>
                  </div>
                  <Button size="sm" className="w-full h-11 md:h-8 bg-primary hover:bg-primary/90" onClick={saveSchedule} disabled={savingSched || removingSched}>
                    {savingSched ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCircle className="h-3.5 w-3.5 mr-1.5" />{t('clientCheckIns.saveSchedule')}</>}
                  </Button>
                  {schedule?.enabled && (
                    <Button size="sm" variant="ghost" className="w-full h-11 md:h-8 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      onClick={async () => {
                        const ok = await confirm({
                          title: t('clientCheckIns.confirmRemoveSchedule'),
                          confirmLabel: t('clientCheckIns.removeSchedule'),
                          cancelLabel: t('common.cancel'),
                          destructive: true,
                        });
                        if (ok) removeScheduleMutation.mutate();
                      }}
                      disabled={savingSched || removingSched}>
                      {removingSched ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><X className="h-3.5 w-3.5 mr-1.5" />{t('clientCheckIns.removeSchedule')}</>}
                    </Button>
                  )}
                </div>
              ) : schedule?.next_check_in ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-2xl font-bold font-mono ${isOverdue ? 'text-red-500' : isDueToday ? 'text-primary' : ''}`}>
                      {isOverdue ? t('clientCheckIns.overdue') : isDueToday ? t('clientCheckIns.dueToday') : countdown}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {format(parseISO(schedule.next_check_in), 'dd MMM yyyy · HH:mm')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('clientCheckIns.everyNDays', { days: schedule.frequency_days, total: checkIns.length })}</p>
                    {(isDueToday || isOverdue) && (
                      <button
                        type="button"
                        onClick={() => {
                          confirm({
                            title: t('clientCheckIns.confirmSkipCheckin'),
                            confirmLabel: t('clientCheckIns.skipThisCheckin'),
                            cancelLabel: t('common.cancel'),
                          }).then((ok) => { if (ok) skipCheckInMutation.mutate(); });
                        }}
                        disabled={skipping}
                        className="mt-2 text-[11px] text-muted-foreground/70 hover:text-muted-foreground underline underline-offset-2 disabled:opacity-50"
                        data-testid="coach-skip-checkin-btn-desktop"
                      >
                        {skipping ? <Loader2 className="h-3 w-3 animate-spin inline" /> : t('clientCheckIns.skipThisCheckin')}
                      </button>
                    )}
                  </div>
                  <div className={`w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 ${isOverdue ? 'border-red-500 bg-red-500/10' : 'border-primary bg-primary/10'}`}>
                    <span className={`text-xl font-bold ${isOverdue ? 'text-red-500' : 'text-primary'}`}>{checkIns.length}</span>
                    <span className="text-[9px] text-muted-foreground leading-none">{t('clientCheckIns.checkInsCount')}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-2">
                  <p className="text-sm text-muted-foreground">{t('clientCheckIns.noSchedule')}</p>
                  <button onClick={() => setEditingSchedule(true)} className="text-xs text-primary font-medium mt-1 hover:underline">
                    {t('clientCheckIns.setSchedule')}
                  </button>
                </div>
              )}
            </div>

            {/* STATS STRIP */}
            {checkIns.length > 0 && (() => {
              const latest = checkIns[0];
              const prev = checkIns[1];
              const weightDiff = latest.weight_kg && prev?.weight_kg ? (latest.weight_kg - prev.weight_kg) : null;
              return (
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-xl bg-muted/40 p-3 text-center">
                    <p className="text-lg font-bold font-mono">{latest.weight_kg ?? '—'}<span className="text-xs text-muted-foreground ml-0.5">kg</span></p>
                    {weightDiff !== null && (
                      <p className={`text-xs font-semibold flex items-center justify-center gap-0.5 ${weightDiff < 0 ? 'text-green-500' : weightDiff > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {weightDiff < 0 ? <TrendingDown className="h-3 w-3" /> : weightDiff > 0 ? <TrendingUp className="h-3 w-3" /> : null}
                        {weightDiff > 0 ? '+' : ''}{weightDiff?.toFixed(1)}kg
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">{t('clientCheckIns.statWeight')}</p>
                  </div>
                  <div className="rounded-xl bg-muted/40 p-3 text-center">
                    <p className="text-2xl">{latest.mood ? MOOD_EMOJI[latest.mood] : '—'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('clientCheckIns.statMood')}</p>
                  </div>
                  <div className="rounded-xl bg-muted/40 p-3 text-center">
                    <p className="text-lg font-bold font-mono">{latest.adherence_percent ?? '—'}<span className="text-xs text-muted-foreground">%</span></p>
                    <p className="text-xs text-muted-foreground">{t('clientCheckIns.statAdherence')}</p>
                  </div>
                </div>
              );
            })()}

            <div>
              <p className="text-sm font-bold mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {t('clientCheckIns.sectionHistory')} <span className="text-muted-foreground font-normal text-xs">({checkIns.length})</span>
              </p>
              <CheckInList
                checkIns={checkIns}
                selectedId={selectedCheckIn?.id}
                onSelect={(id) => openCi(id)}
                variant="desktop"
                rowRefs={rowRefs}
              />
            </div>
          </aside>

          {/* Right detail pane */}
          <main className="overflow-y-auto">
            <div className="max-w-5xl mx-auto p-6 space-y-4">
              {selectedCheckIn && (
                <div className="flex items-center gap-3 pb-3 border-b border-border/50">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-muted/60 flex flex-col items-center justify-center">
                    <span className="text-sm font-bold leading-none">{selectedCheckIn.date?.slice(8, 10)}</span>
                    <span className="text-[10px] text-muted-foreground uppercase">{selectedCheckIn.date ? format(new Date(selectedCheckIn.date), 'MMM') : ''}</span>
                    <span className="text-[10px] text-muted-foreground">{selectedCheckIn.date?.slice(0, 4)}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-3 flex-wrap">
                    {selectedCheckIn.weight_kg && <span className="text-xl font-bold font-mono">{selectedCheckIn.weight_kg}kg</span>}
                    {selectedCheckIn.mood && <span className="text-2xl">{MOOD_EMOJI[selectedCheckIn.mood]}</span>}
                    {selectedCheckIn.adherence_percent != null && (
                      <Badge variant="outline" className="text-xs">{selectedCheckIn.adherence_percent}% {t('clientCheckIns.adherenceSuffix')}</Badge>
                    )}
                  </div>
                </div>
              )}

              <CheckInDetail
                selectedCheckIn={selectedCheckIn}
                allCheckIns={checkIns}
                clientName={clientProp?.name}
                clientId={clientProp?.id}
                onSaveFeedback={handleSaveFeedback}
                onOpenLightbox={(photos, index) => {
                  setLightboxPhotos(photos);
                  setLightboxIndex(index);
                }}
                onSelect={(id) => openCi(id)}
              />
            </div>
          </main>
        </div>

      {/* ── Photo Lightbox ───────────────────────────── */}
      {lightboxPhotos && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center"
          onClick={() => setLightboxPhotos(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
        >
          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10" onClick={e => e.stopPropagation()}>
            <span className="text-white/60 text-xs font-mono">{lightboxIndex + 1} / {lightboxPhotos.length}</span>
            <button
              autoFocus
              onClick={() => setLightboxPhotos(null)}
              className="text-white/70 hover:text-white p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Photo with overlay arrows */}
          <div
            className="relative flex items-center justify-center w-full px-0"
            onClick={e => e.stopPropagation()}
            onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={e => {
              if (touchStartX.current === null) return;
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              touchStartX.current = null;
              if (dx < -50) setLightboxIndex(i => Math.min(i + 1, lightboxPhotos.length - 1));
              else if (dx > 50) setLightboxIndex(i => Math.max(i - 1, 0));
            }}
          >
            <img
              src={lightboxPhotos[lightboxIndex].url}
              alt={lightboxPhotos[lightboxIndex].label}
              className="max-h-[85dvh] max-w-full object-contain rounded-lg"
            />

            {/* Left arrow overlay */}
            <button
              onClick={() => setLightboxIndex(i => Math.max(i - 1, 0))}
              disabled={lightboxIndex === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white disabled:opacity-20 rounded-full p-2"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>

            {/* Right arrow overlay */}
            <button
              onClick={() => setLightboxIndex(i => Math.min(i + 1, lightboxPhotos.length - 1))}
              disabled={lightboxIndex === lightboxPhotos.length - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/70 text-white disabled:opacity-20 rounded-full p-2"
            >
              <ChevronRight className="h-7 w-7" />
            </button>
          </div>

          {/* Label */}
          <div className="absolute bottom-6 left-0 right-0 flex justify-center" onClick={e => e.stopPropagation()}>
            <span className="bg-black/50 text-white text-xs px-3 py-1 rounded-full uppercase tracking-wide">
              {lightboxPhotos[lightboxIndex].label}
            </span>
          </div>
        </div>
      )}
      </SheetContent>
    </Sheet>
  );
}

export default ClientCheckInsSheet;

